import { Box, Grid, Paper, Skeleton, Typography, useTheme } from "@mui/material";
import DataGridCustomPresence from "src/components/data-grid/data-grid-custom-presence";
import Iconify from "src/components/iconify";
import { useCountriesQuery, useGetPresenceQuery, useSearchTermsQuery } from "src/context/api/competitorsMonitoring/api";
import { useDispatch, useSelector } from "react-redux";
import { fDate } from "src/utils/format-time";
import { useCurrentWorkspace } from "src/context/reducers/app-settings";
import { useEffect } from "react";
import { setFilterCompare, setFilterDate, setFilterDateCompare, setFilterLoadedCountry, setFilterLoadedKeyword, setFilterMinDatePicker } from "src/context/reducers/filter-settings/filterSlice";
import { useLocales } from "src/locales";
import { usePreviousLocation } from "src/components/previousLocationContext";
import { subDays } from "date-fns";

export default function CompetitorFocus() {
    const { t } = useLocales();
    const theme = useTheme();
    const filterStore = useSelector((state: any) => state.filters);
    const workspace: any = useCurrentWorkspace();
    const dispatch = useDispatch();

    const skipQuery = !filterStore.start || !filterStore.end || !workspace?.id;

    const { data: presenceData, error: dataPresenceError, isFetching: isFetchingDataPresence } = useGetPresenceQuery({
        startDate: filterStore.start !== undefined ? fDate(filterStore.start, "yyyy-MM-dd") : null,
        endDate: filterStore.end !== undefined ? fDate(filterStore.end, "yyyy-MM-dd") : null,
        compareStartDate: filterStore.compareStart !== undefined ? fDate(filterStore.compareStart, "yyyy-MM-dd") : null,
        compareEndDate: filterStore.compareEnd !== undefined ? fDate(filterStore.compareEnd, "yyyy-MM-dd") : null,
        timestep: filterStore.timestep,
        device: Array.isArray(filterStore.devices) ? filterStore.devices.map((device: any) => device.toLowerCase()) : filterStore.devices?.toLowerCase(),
        countries: filterStore.countries,
        keywords: filterStore.keywords,
        workspaceId: workspace?.id,
        domain: workspace?.url,
        platform: filterStore?.platforms,
        campaignType: null
    }, {
        skip: skipQuery,
        refetchOnMountOrArgChange: true,
    });

    const dataPresence = presenceData?.presence?.map((item: any, index: any) => ({
        id: index,
        domain: item?.domain,
        presenceRate: item?.ratio_present_scrap * 100,
        device: item?.device,
        country: item?.country,
    })) || [];

    const updatedPresence = dataPresence.map((domainData: any) => {
        const matchSearchDomain = presenceData?.presenceSearch?.find((item: any) => item.domain === domainData.domain)
        const matchShoppingDomain = presenceData?.presenceShopping?.find((item: any) => item.domain === domainData.domain)

        return {
            ...domainData,
            presenceSearchRate: matchSearchDomain?.ratio_present_search_scrap * 100 || 0,
            presenceShoppingRate: matchShoppingDomain?.ratio_present_shopping_scrap * 100 || 0
        }
    })

    useEffect(() => {
        if (presenceData?.minDatePicker) {
            dispatch(setFilterMinDatePicker(presenceData.minDatePicker[0]?.first_date));
        }
    }, [presenceData, dispatch]);

    let competitorsOverviewDatas = dataPresence

    if (workspace?.template === 'E-commerce' || workspace?.shoppingIncluded === true) {
        competitorsOverviewDatas = updatedPresence
    }

    useEffect(() => {
        dispatch(setFilterDate({
            start: new Date(new Date().setDate(new Date().getDate() - 30)).toISOString()
        }))

        dispatch(setFilterCompare(false))

        dispatch(setFilterDateCompare({
            compareStart: subDays(new Date(), 60).toISOString(),
            compareEnd: subDays(new Date(), 31).toISOString(),
        }))
    }, [])

    const { data: countries } = useCountriesQuery(
        {
            workspace: workspace?.bqTable,
            platforms: filterStore?.platforms,
            startDate: filterStore?.start,
            endDate: filterStore?.end
        },
        { skip: !workspace?.bqTable }
    );

    const { data: searchTerms } = useSearchTermsQuery(
        {
            workspace: workspace?.bqTable,
            platforms: filterStore?.platforms,
            devices: filterStore?.devices,
            startDate: filterStore?.start,
            endDate: filterStore?.end
        },
        { skip: !workspace?.bqTable }
    );

    // FILTER COUNTRIES
    useEffect(() => {
        if (countries?.countries?.length > 0) {
            dispatch(setFilterLoadedCountry(countries.countries));
        }
    }, [countries?.countries, workspace]);

    // FILTER KEYWORDS
    useEffect(() => {
        if (searchTerms?.search_terms?.length > 0) {
            dispatch(setFilterLoadedKeyword(searchTerms?.search_terms))
        }
    }, [searchTerms?.search_terms, workspace]);

    return (
        <>
            <Box>

                <Grid item xs={12} md={6} lg={6}>
                    <Paper
                        sx={{
                            backgroundColor: theme.palette.mode === "dark" ? '#212b36' : 'white',
                            boxShadow: '0px 0px 2px 0px rgba(145, 158, 171, 0.2), 0px 12px 24px -4px rgba(145, 158, 171, 0.12)',
                            borderRadius: 2,
                            marginBottom: 1,
                            width: '100%',
                            height: 830
                        }}
                    >
                        {isFetchingDataPresence || (!dataPresence && !dataPresenceError)
                            ? <Skeleton width={'100%'} height={'100%'} style={{ borderRadius: 10 }} variant="rectangular" animation={"wave"}>
                                <>
                                    <Iconify py={"auto"} icon={"eva:checkmark-circle-2-fill"} color={"success.main"} />
                                    <Typography color={"success.main"}>Active</Typography>
                                </>
                            </Skeleton>
                            : <div>
                                <div style={{ paddingTop: 18, paddingLeft: 25, paddingRight: 25 }}>
                                    <Typography variant="overline" color="textSecondary" sx={{ mb: 1 }}>{t('dashboard.competitors')}</Typography>

                                    <Typography variant="h6" sx={{ mb: 5 }}>
                                        {t('dashboard.competitorsOverview')}
                                    </Typography>

                                </div>

                                <Box sx={{ height: 600, }}>
                                    <DataGridCustomPresence pageSize={10} data={competitorsOverviewDatas} />
                                </Box>
                            </div>
                        }
                    </Paper>
                </Grid>
            </Box>
        </>
    )
}
